import { useState } from "react";

interface LayerItem {
  id: string;
  label: string;
  color: string;
  children?: { id: string; label: string; color: string }[];
}

const layerData: LayerItem[] = [
  // 点
  { id: "louyu_dian", label: "楼栋地址", color: "#ff6b6b" },
  { id: "poi_dian", label: "POI", color: "#34d399" },
  
  // 面
  { id: "jianzhuwu_mian", label: "建筑物", color: "#94a3b8" },
  { id: "jianzhuwu_baimo", label: "建筑物白膜", color: "#64748b" },
  { id: "jumindi_mian", label: "居民地", color: "#4ecdc4" },
  { id: "changjing_mian", label: "场景面", color: "#f59e0b" },
  
  //路网
  {
    id: "luwang",
    label: "路网",
    color: "#60a5fa",
    children: [
      { id: "ditie_dian", label: "地铁点", color: "#60a5fa" },
      { id: "ditie_xian", label: "地铁线", color: "#60a5fa" },
      { id: "ditie_mian", label: "地铁面", color: "#60a5fa" },
      { id: "tielu_dian", label: "火车站点", color: "#a78bfa" },
      { id: "tielu_xian", label: "火车站线", color: "#a78bfa" },
      { id: "tielu_mian", label: "火车站面", color: "#a78bfa" },
      { id: "gonglu_xian", label: "道路中心线", color: "#10b981" },
      { id: "gonglu_mian", label: "道路面", color: "#45b7d1" },
    ],
  },

  //五级行政区划
  {
    id: "wujixingzhengquhua",
    label: "五级行政区划",
    color: "#45b7d1",
    children: [
      { id: "quxian_dian", label: "区县点", color: "#3b82f6" },
      { id: "xiangzhen_mian", label: "乡镇界", color: "#7c3aed" },
      { id: "quxian_mian", label: "区县界", color: "#3b82f6" },
      { id: "dishi_mian", label: "地市界", color: "#ef4444" },
    ],
  },
  { id: "dem", label: "DEM", color: "#fbbf24" },
  // 新增的动态数据图层 结束
  /* { id: "commercial", label: "商务楼宇", color: "#60a5fa" },
  { id: "street_shop", label: "沿街商铺", color: "#34d399" },
  { id: "road_network", label: "路网", color: "#94a3b8" },
  { id: "building", label: "建筑物", color: "#64748b" },
  { id: "building_white", label: "建筑物白膜", color: "#cbd5e1" }, */

];

interface LayerControlProps {
  checked: Record<string, boolean>;
  onCheckedChange: (checked: Record<string, boolean>) => void;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export default function LayerControl({ checked, onCheckedChange, collapsed: collapsedProp, onCollapsedChange }: LayerControlProps) {
  const [localCollapsed, setLocalCollapsed] = useState(false);
  const collapsed = collapsedProp !== undefined ? collapsedProp : localCollapsed;
  const setCollapsed = (val: boolean) => {
    if (onCollapsedChange) {
      onCollapsedChange(val);
    } else {
      setLocalCollapsed(val);
    }
  };
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggle = (id: string) => {
    const layer = layerData.find(l => l.id === id);
    if (layer && layer.children) {
      // 如果是父级且有子集，切换父级和所有子集的状态
      const newChecked = { ...checked };
      const targetState = !checked[id];

      // 切换父级状态
      newChecked[id] = targetState;

      // 切换所有子集状态
      layer.children.forEach(child => {
        newChecked[child.id] = targetState;
      });

      onCheckedChange(newChecked);
    } else {
      // 如果是普通项或没有子集的父级，只切换自身状态
      onCheckedChange({ ...checked, [id]: !checked[id] });
    }
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="absolute top-36 right-4 z-30">
      {collapsed ? (
        <button
          onClick={() => setCollapsed(false)}
          className="flex flex-col items-center justify-center w-8 bg-white/95 backdrop-blur-md border border-slate-200 rounded-lg py-3 text-slate-600 hover:text-slate-800 hover:border-slate-300 transition-colors cursor-pointer"
        >
          <i className="ri-stack-line text-base"></i>
          <span className="text-xs mt-1" style={{ writingMode: "vertical-rl" }}>数据图层</span>
        </button>
      ) : (
        <div className="w-40 bg-white/95 backdrop-blur-md border border-slate-200/80 rounded-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-2.5 py-2 border-b border-slate-100">
            <div className="flex items-center gap-1.5">
              <i className="ri-stack-line text-slate-500 text-sm"></i>
              <span className="text-slate-800 text-xs font-semibold">数据图层</span>
            </div>
            <button
              onClick={() => setCollapsed(true)}
              className="w-4 h-4 flex items-center justify-center text-slate-400 hover:text-slate-700 cursor-pointer"
            >
              <i className="ri-subtract-line text-xs"></i>
            </button>
          </div>

          {/* Layer List */}
          <div className="py-1 max-h-[calc(100vh-260px)] overflow-y-auto">
            {layerData.map((layer) => (
              <div key={layer.id}>
                <div className="flex items-center gap-1 px-2 py-1 hover:bg-slate-50 transition-colors group">
                  {/* Expand toggle */}
                  {layer.children ? (
                    <button
                      onClick={() => toggleExpand(layer.id)}
                      className="w-3.5 h-3.5 flex items-center justify-center text-slate-400 hover:text-slate-700 cursor-pointer flex-shrink-0"
                    >
                      <i className={`ri-arrow-right-s-line text-xs transition-transform duration-150 ${expanded[layer.id] ? "rotate-90" : ""}`}></i>
                    </button>
                  ) : (
                    <span className="w-3.5 flex-shrink-0"></span>
                  )}

                  {/* Checkbox */}
                  <button
                    onClick={() => toggle(layer.id)}
                    className={`w-3.5 h-3.5 rounded flex-shrink-0 border flex items-center justify-center transition-all cursor-pointer ${checked[layer.id]
                        ? "border-transparent"
                        : "border-slate-300 bg-transparent"
                      }`}
                    style={checked[layer.id] ? { backgroundColor: layer.color } : {}}
                  >
                    {checked[layer.id] && <i className="ri-check-line text-white leading-none" style={{ fontSize: "8px" }}></i>}
                  </button>

                  {/* Color dot */}
                  {/* <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: layer.color }}></span> */}

                  {/* Label */}
                  <span
                    className={`text-xs font-medium cursor-pointer select-none leading-tight truncate ${checked[layer.id] ? "text-slate-800" : "text-slate-400"
                      }`}
                    onClick={() => toggle(layer.id)}
                  >
                    {layer.label}
                  </span>
                </div>

                {/* Children */}
                {layer.children && expanded[layer.id] && (
                  <div className="bg-slate-50/80">
                    {layer.children.map((child) => (
                      <div key={child.id} className="flex items-center gap-1.5 px-2 py-1 pl-7 hover:bg-slate-100 transition-colors">
                        <button
                          onClick={() => toggle(child.id)}
                          className={`w-3 h-3 rounded flex-shrink-0 border flex items-center justify-center transition-all cursor-pointer ${checked[child.id]
                              ? "border-transparent"
                              : "border-slate-300 bg-white"
                            }`}
                          style={checked[child.id] ? { backgroundColor: child.color } : {}}
                        >
                          {checked[child.id] && <i className="ri-check-line text-white leading-none" style={{ fontSize: "7px" }}></i>}
                        </button>
                        {/* Color dot for children */}
                        {/* <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: child.color }}></span> */}
                        <span
                          className={`text-xs cursor-pointer select-none ${checked[child.id] ? "text-slate-700" : "text-slate-500"
                            }`}
                          onClick={() => toggle(child.id)}
                        >
                          {child.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}